/**
 * The three assessment dimensions, the weakest-dimension rule, and the
 * override record (HG-3A G3-02).
 *
 * Requirement sources, in precedence order:
 *   - Signed ICD v1.0 section 8.7, which is the only place the rule's
 *     arithmetic is defined: "Each dimension scale has five ordered points and
 *     the grade scale has five ordered points; the WingMentor rule is
 *     positional: the proposed grade is the lowest of the three ordinal
 *     positions."
 *   - Guide Change Specification v1.3 section 4.2, GCH-11 to GCH-13. GCH-13
 *     words the proposal as permissive and the disclosure as mandatory: Guide
 *     "may propose a grade ... and shall show the rule". Hence
 *     WEAKEST_DIMENSION_RULE_STATEMENT below is not optional UI decoration; it
 *     is the requirement.
 *   - ICD section 8.7 second bullet on ties, which is the subtle one: the
 *     limiting factor is the instructor's recorded judgment, "stored, never
 *     recomputed, and a future recomputation must not overwrite it". Nothing
 *     in this module ever derives the limiting factor. It reports which
 *     dimensions are tied at the minimum so the UI can ask; the answer is the
 *     instructor's.
 *
 * Vocabulary comes from packages/shared/src/icd/enums.ts and is never
 * redefined here. Scale positions come from the registry via the framework
 * loader, never from a local table: a tenant framework version may word its
 * scales differently, and position, not code order, is what the rule uses.
 */

import type { ResolvedDimensionStep, ResolvedFramework } from '../framework/loader';

/** The three dimensions, in the order they are captured and presented. */
export const DIMENSION_KEYS = ['how_many', 'how_often', 'tem_outcome'] as const;

export type DimensionKey = (typeof DIMENSION_KEYS)[number];

/** A set of dimension selections, each an ICD enum code, any of them absent. */
export type DimensionSelection = Partial<Record<DimensionKey, string | null>>;

/**
 * The rule statement Guide is required to show wherever a proposal appears
 * (GCH-13: "and shall show the rule"). Kept here so web and mobile cannot
 * drift into two different explanations of the same arithmetic.
 */
export const WEAKEST_DIMENSION_RULE_STATEMENT =
  'Weakest-dimension rule: the proposed grade is the lowest of the three dimension positions. The recorded grade is always the instructor’s.';

export interface DimensionOption {
  /** ICD enum code, the stored value. */
  code: string;
  /** 1 (worst) to 5 (best), from the registry. */
  position: number;
  /** ICAO Doc 9995 Appendix 4 wording, read from the registry at run time. */
  wordPicture: string;
}

export interface GradeProposal {
  /**
   * The proposed grade, or null when the proposal is not yet available.
   * GCH-13 makes proposing permissive, so null is a legitimate state and not
   * an error: there is simply nothing to show yet.
   */
  proposedGrade: number | null;
  /** True once all three dimensions carry a selection. */
  complete: boolean;
  /**
   * Which dimensions sit at the lowest position. One entry is the ordinary
   * case. Two or three entries mean the instructor must record which one drove
   * the grade, because ICD 8.7 forbids deriving it. Empty while incomplete.
   */
  tiedAtMinimum: DimensionKey[];
  /**
   * The single dimension at the minimum, when there is exactly one. Offered as
   * a DEFAULT for the limiting-factor selector, never as a stored value: what
   * is stored is whatever the instructor confirms.
   */
  suggestedLimitingFactor: DimensionKey | null;
}

/**
 * Options for one dimension, worst to best by registry position.
 * Returns an empty array when the resolved framework carries no scale for the
 * dimension, which renders as nothing rather than as invented wording.
 */
export function dimensionOptions(
  framework: Pick<ResolvedFramework, 'dimensionScales'> | null | undefined,
  dimension: DimensionKey,
): DimensionOption[] {
  const steps: ResolvedDimensionStep[] = framework?.dimensionScales ?? [];
  return steps
    .filter((step) => step.dimension === dimension)
    .slice()
    .sort((a, b) => a.position - b.position)
    .map((step) => ({
      code: step.code,
      position: step.position,
      wordPicture: step.wordPicture,
    }));
}

/**
 * The registry position of one selected code, or null when the code does not
 * resolve in this framework version. Null propagates: an unresolvable code
 * yields no proposal rather than a wrong one.
 */
export function dimensionPosition(
  framework: Pick<ResolvedFramework, 'dimensionScales'> | null | undefined,
  dimension: DimensionKey,
  code: string | null | undefined,
): number | null {
  if (!code) return null;
  const step = (framework?.dimensionScales ?? []).find(
    (s) => s.dimension === dimension && s.code === code,
  );
  return step ? step.position : null;
}

/**
 * Apply the weakest-dimension rule (ICD 8.7).
 *
 * No proposal is produced until all three dimensions are selected AND all
 * three resolve in the framework version. A partial set has no defined lowest
 * position, and proposing from two dimensions would be a different rule.
 */
export function proposeGrade(
  framework: Pick<ResolvedFramework, 'dimensionScales'> | null | undefined,
  selection: DimensionSelection,
): GradeProposal {
  const positions = DIMENSION_KEYS.map((key) => ({
    key,
    position: dimensionPosition(framework, key, selection[key]),
  }));

  const resolved = positions.filter(
    (p): p is { key: DimensionKey; position: number } => p.position != null,
  );

  if (resolved.length !== DIMENSION_KEYS.length) {
    return {
      proposedGrade: null,
      complete: false,
      tiedAtMinimum: [],
      suggestedLimitingFactor: null,
    };
  }

  const minimum = Math.min(...resolved.map((p) => p.position));
  const tiedAtMinimum = resolved.filter((p) => p.position === minimum).map((p) => p.key);

  return {
    proposedGrade: minimum,
    complete: true,
    tiedAtMinimum,
    suggestedLimitingFactor: tiedAtMinimum.length === 1 ? tiedAtMinimum[0] ?? null : null,
  };
}

// ---------------------------------------------------------------------------
// The override record (GCH-13, DEC-28, ICD 3.1.2)
// ---------------------------------------------------------------------------

/** ICD 8.4 OverrideCategoryV1. Re-exported shape, not a redefinition. */
export type OverrideCategory =
  | 'evidence_not_captured_by_dimensions'
  | 'dimension_recorded_in_error'
  | 'tem_outcome_not_attributable'
  | 'other';

export interface OverrideRecordInput {
  /** The instructor's recorded grade. Always authoritative (ICD 8.7). */
  recordedGrade: number | null;
  /** What the rule produced, or null where no proposal was shown. */
  proposedGrade: number | null;
  category?: OverrideCategory | null;
  note?: string | null;
  actor?: string | null;
  at?: string | null;
}

export interface OverrideRecord {
  proposed_grade: number | null;
  override_flag: boolean | null;
  override_category: OverrideCategory | null;
  override_note: string | null;
  override_actor: string | null;
  override_at: string | null;
}

export interface OverrideValidation {
  valid: boolean;
  /** Machine-readable reasons, empty when valid. */
  errors: string[];
}

/**
 * Whether the recorded grade diverges from the proposal.
 * False when there is no proposal to diverge from: an assessment recorded
 * without a shown proposal is not an override, it is simply a grade.
 */
export function isOverride(recordedGrade: number | null, proposedGrade: number | null): boolean {
  if (recordedGrade == null || proposedGrade == null) return false;
  return recordedGrade !== proposedGrade;
}

/**
 * Validate an override record against DEC-28, which this codebase enforces in
 * three places that must agree: here, the CHECK constraints of migration 248,
 * and migration 241's identical constraints on the Elevate copy.
 *
 *   - a category is mandatory on every override, of any size;
 *   - a category is forbidden when there is no override;
 *   - free text is required if and only if the category is `other`.
 *
 * The Core Framework v3.4 rule demanding a free-text justification for
 * overrides of two or more points is withdrawn (Change Spec v1.3 section 9),
 * so no size threshold appears anywhere in this function.
 */
export function validateOverride(input: OverrideRecordInput): OverrideValidation {
  const errors: string[] = [];
  const overridden = isOverride(input.recordedGrade, input.proposedGrade);
  const category = input.category ?? null;
  const note = input.note?.trim() ? input.note.trim() : null;

  if (overridden && !category) {
    errors.push('OVERRIDE_CATEGORY_REQUIRED');
  }
  if (!overridden && category) {
    errors.push('OVERRIDE_CATEGORY_NOT_PERMITTED');
  }
  if (category === 'other' && !note) {
    errors.push('OVERRIDE_NOTE_REQUIRED_FOR_OTHER');
  }
  if (category !== 'other' && note) {
    errors.push('OVERRIDE_NOTE_ONLY_FOR_OTHER');
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Build the row fragment written to assessments_pilot. Emits the ICD 3.1.2
 * column names so the write path, the contract view and migration 248 all
 * speak one vocabulary.
 *
 * Called by the web save path (frontend/src/lib/session-assessment-draft.ts)
 * and the mobile one (mobile/src/components/wizard/PilotAssessmentStep.tsx)
 * since G6-04. Before that it emitted all six elements and had no caller
 * outside its own tests while both clients hand-built a four-field copy, which
 * is how override_actor and override_at came to be null on every stored row
 * (GAT-12). Anything that needs an override record builds it here.
 *
 * Note what is deliberately absent: no final_grade. The recorded grade is
 * assessments_pilot.score and adding a second home for it would create two
 * candidate truths for one fact.
 */
export function buildOverrideRecord(input: OverrideRecordInput): OverrideRecord {
  const overridden = isOverride(input.recordedGrade, input.proposedGrade);
  const category = overridden ? (input.category ?? null) : null;
  const note = category === 'other' && input.note?.trim() ? input.note.trim() : null;

  return {
    proposed_grade: input.proposedGrade,
    // Null only where the record is historical; a live capture always states
    // the flag, even when false (ICD 9.3, INT-08).
    override_flag: overridden,
    override_category: category,
    override_note: note,
    override_actor: overridden ? (input.actor ?? null) : null,
    override_at: overridden ? (input.at ?? null) : null,
  };
}

/**
 * Whether a stored row has a complete dimensions group. Mirrors migration
 * 248's ap_dimensions_group_together: all four present or all four absent,
 * never a partial group.
 */
export function hasCompleteDimensions(row: {
  how_many?: string | null;
  how_often?: string | null;
  tem_outcome?: string | null;
  limiting_factor?: string | null;
}): boolean {
  const values = [row.how_many, row.how_often, row.tem_outcome, row.limiting_factor];
  return values.every((v) => v != null && v !== '');
}

/**
 * Presentation rule for a value that was never recorded (GCH-35): the words
 * "not recorded", never zero, a default, or an inferred value. Exported so
 * every surface says the same thing.
 */
export const NOT_RECORDED_LABEL = 'Not recorded';
